'use client';
import { usePathname } from 'next/navigation';
import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  FC,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
export interface SidebarComponentProps<T = unknown> {
  sidebar: boolean;
  setSidebarWidth: React.Dispatch<React.SetStateAction<number>>;
  sidebarWidth: number;
  params: T;
}
interface SidebarItem<T = unknown> {
  id: number;
  component: React.FC<SidebarComponentProps<T>>;
  params?: T;
  isClosing?: boolean;
}
interface SidebarManagerContextValue {
  openedSidebars: SidebarItem<unknown>[];
  openSidebar: <T>(
    component: React.FC<SidebarComponentProps<T>>,
    closeOthers: boolean,
    params?: T,
  ) => void;
  closeSidebar: <T>(component: React.FC<SidebarComponentProps<T>>) => void;
  closeMultipleSidebars: <T>(
    components: React.FC<SidebarComponentProps<T>>[],
  ) => void;
  closeAllSidebars: () => void;
  closeAllSidebarsExceptFirst: () => void;
}
interface SidebarProps {
  children?: ReactNode;
}

const NavigationHandler: FC<{ closeAllSidebars: () => void }> = ({
  closeAllSidebars,
}) => {
  const pathname = usePathname();

  useEffect(() => {
    closeAllSidebars();
  }, [pathname, closeAllSidebars]);

  return null;
};

const SidebarManagerContext = createContext<SidebarManagerContextValue | null>(
  null,
);
const fallbackContext: SidebarManagerContextValue = {
  openedSidebars: [],
  openSidebar: () => {},
  closeSidebar: () => {},
  closeMultipleSidebars: () => {},
  closeAllSidebars: () => {},
  closeAllSidebarsExceptFirst: () => {},
};

export const useSidebarManager = () => {
  const context = useContext(SidebarManagerContext);
  if (!context) {
    console.error(
      'useSidebarManager must be used within a SidebarManagerProvider',
    );
    return fallbackContext;
  }
  return context;
};
export const SidebarManagerProvider: FC<SidebarProps> = ({ children }) => {
  const [openedSidebars, setOpenedSidebars] = useState<SidebarItem<unknown>[]>(
    [],
  );
  const ANIMATION_DURATION = 300;
  const idCounter = useRef(0);

  const nextId = useCallback(() => {
    idCounter.current += 1;
    return idCounter.current;
  }, []);

  const openSidebar = useCallback(
    <T,>(
      component: React.FC<SidebarComponentProps<T>>,
      closeOthers: boolean,
      params?: T,
    ) => {
      setOpenedSidebars((prev) => {
        if (closeOthers) {
          // Close all others and open only this one
          const newItem: SidebarItem<unknown> = {
            id: nextId(),
            component,
            params,
            isClosing: false,
          } as SidebarItem<unknown>;
          return [newItem];
        }

        // Check if this sidebar is already open (not closing)
        const existingIndex = prev.findIndex(
          (item) => item.component === component && !item.isClosing,
        );

        if (existingIndex !== -1) {
          // Already open, update params if changed
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            params,
          };
          return updated;
        }

        // Remove any closing versions and add the new one
        const filtered = prev.filter((item) => item.component !== component);

        const newItem: SidebarItem<unknown> = {
          id: nextId(),
          component,
          params,
          isClosing: false,
        } as SidebarItem<unknown>;

        return [...filtered, newItem];
      });
    },
    [nextId],
  );

  const closeSidebar = useCallback(
    <T,>(component: React.FC<SidebarComponentProps<T>>) => {
      setOpenedSidebars((prev) => {
        const closingIndex = prev.findIndex(
          (sidebar) => sidebar.component === component && !sidebar.isClosing,
        );

        if (closingIndex === -1) return prev;

        // If closing the first sidebar (primary) and there are other sidebars, close all
        if (closingIndex === 0 && prev.length > 1) {
          const updated = prev.map((sidebar) => ({
            ...sidebar,
            isClosing: true,
          }));

          setTimeout(() => {
            setOpenedSidebars([]);
            document.body.classList.remove('minthr-sidebar-opened');
          }, ANIMATION_DURATION);

          return updated;
        }

        // Mark matching sidebar as closing
        const updated = prev.map((sidebar) =>
          sidebar.component === component && !sidebar.isClosing
            ? { ...sidebar, isClosing: true }
            : sidebar,
        );

        // Remove after animation
        setTimeout(() => {
          setOpenedSidebars((current) => {
            const filtered = current.filter(
              (sidebar) =>
                !(sidebar.component === component && sidebar.isClosing),
            );

            if (filtered.length === 0) {
              document.body.classList.remove('minthr-sidebar-opened');
            }

            return filtered;
          });
        }, ANIMATION_DURATION);

        return updated;
      });
    },
    [],
  );

  const closeMultipleSidebars = useCallback(
    <T,>(components: React.FC<SidebarComponentProps<T>>[]) => {
      setOpenedSidebars((prev) => {
        const hasMatch = prev.some(
          (sidebar) =>
            !sidebar.isClosing &&
            components.some((component) => sidebar.component === component),
        );

        if (!hasMatch) return prev;

        // Mark matching sidebars as closing
        const updated = prev.map((sidebar) =>
          !sidebar.isClosing &&
          components.some((component) => sidebar.component === component)
            ? { ...sidebar, isClosing: true }
            : sidebar,
        );

        // Remove after animation
        setTimeout(() => {
          setOpenedSidebars((current) => {
            const filtered = current.filter(
              (sidebar) =>
                !(
                  sidebar.isClosing &&
                  components.some(
                    (component) => sidebar.component === component,
                  )
                ),
            );

            if (filtered.length === 0) {
              document.body.classList.remove('minthr-sidebar-opened');
            }

            return filtered;
          });
        }, ANIMATION_DURATION);

        return updated;
      });
    },
    [],
  );

  const closeAllSidebars = useCallback(() => {
    setOpenedSidebars((prev) => {
      if (prev.length === 0) return prev;

      const updated = prev.map((s) => ({ ...s, isClosing: true }));

      setTimeout(() => {
        setOpenedSidebars([]);
        document.body.classList.remove('minthr-sidebar-opened');
      }, ANIMATION_DURATION);

      return updated;
    });
  }, []);

  const closeAllSidebarsExceptFirst = useCallback(() => {
    setOpenedSidebars((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      return [prev[0]];
    });
  }, []);

  return (
    <SidebarManagerContext.Provider
      value={{
        openedSidebars,
        openSidebar,
        closeSidebar,
        closeMultipleSidebars,
        closeAllSidebars,
        closeAllSidebarsExceptFirst,
      }}
    >
      <NavigationHandler closeAllSidebars={closeAllSidebars} />
      {children}
    </SidebarManagerContext.Provider>
  );
};
